/**
 * DIEUKYDIEU.TV - APPLE NEWS FLOATING READER TOOLBAR MODULE (ES6)
 * Smart dynamic scroll, Apple Reader Popover, 4-step font resizing,
 * Serif/Sans switching, TTS Voice Reading and Multi-device dock sync.
 */

import { showToast } from './ui.js';
import { changeStoryFontSize, resetStoryFontSize } from './story.js';

let lastScrollY = 0;
let scrollTicking = false;

/**
 * Toggle Apple Reader Popover
 */
export function toggleStoryReaderPopover(e) {
    if (e) {
        e.preventDefault();
        e.stopPropagation();
    }
    const popover = document.getElementById('apple-reader-popover');
    const btn = document.getElementById('story-aa-btn');
    if (!popover) return;

    const isShown = popover.classList.contains('show');
    if (isShown) {
        closeStoryReaderPopover();
    } else {
        popover.classList.add('show');
        if (btn) btn.setAttribute('aria-expanded', 'true');
    }
}

/**
 * Close Apple Reader Popover
 */
export function closeStoryReaderPopover() {
    const popover = document.getElementById('apple-reader-popover');
    const btn = document.getElementById('story-aa-btn');
    if (popover && popover.classList.contains('show')) {
        popover.classList.remove('show');
        if (btn) btn.setAttribute('aria-expanded', 'false');
    }
}

/**
 * Set exact font size step from Popover track (-1: 15px, 0: 17px, 1: 19px, 2: 21px)
 */
export function setStoryFontSizeStep(step) {
    let targetStep = Math.max(-1, Math.min(2, parseInt(step, 10)));
    let currentLevel = 0;
    try {
        const saved = parseInt(localStorage.getItem('dieukydieu_font_level'), 10);
        if (!isNaN(saved)) currentLevel = saved;
    } catch (_) {}

    const delta = targetStep - currentLevel;
    if (delta !== 0) {
        changeStoryFontSize(delta);
    }
    updateReaderPopoverUI(targetStep);
}

/**
 * Update active state on stepper dots and label
 */
export function updateReaderPopoverUI(level) {
    const label = document.getElementById('reader-font-val-label');
    const sizeMap = {
        '-1': '15px (Nhỏ)',
        '0': '17px (Chuẩn)',
        '1': '19px (Lớn)',
        '2': '21px (Rất lớn)'
    };
    if (label) {
        label.innerText = sizeMap[level.toString()] || '17px';
    }

    // Step dots in Popover
    document.querySelectorAll('.font-step-track .step-dot').forEach(dot => {
        const stepVal = parseInt(dot.getAttribute('data-step'), 10);
        dot.classList.toggle('active', stepVal === level);
    });
}

/**
 * Switch Story Font Family: Serif (Báo chí) vs Sans (Hiện đại)
 */
export function setStoryFontFamily(family) {
    const container = document.querySelector('.story-detail-container');
    const serifBtn = document.getElementById('font-family-serif');
    const sansBtn = document.getElementById('font-family-sans');

    const isSans = (family === 'sans');
    if (container) {
        container.classList.toggle('font-sans-mode', isSans);
        container.classList.toggle('font-serif-mode', !isSans);
    }

    if (serifBtn) serifBtn.classList.toggle('active', !isSans);
    if (sansBtn) sansBtn.classList.toggle('active', isSans);

    try {
        localStorage.setItem('dieukydieu_font_family', isSans ? 'sans' : 'serif');
    } catch (_) {}

    showToast(isSans ? 'Kiểu chữ: Không chân (Sans)' : 'Kiểu chữ: Có chân (Serif báo chí)');
}

/**
 * Audio TTS Voice Reading Stub (Feature removed from floating capsule)
 */
export function handleStoryAudioPlay(e) {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
}

export function stopStorySpeech() {
    // No-op stub
}

/**
 * Smart Dynamic Scroll Handler:
 * - On Mobile (< 768px): Dynamic scroll (scrolling down hides toolbar smoothly, scrolling up reveals).
 * - On iPad & Desktop (>= 768px): Always floating fixed at top.
 */
function handleSmartScroll() {
    const currentY = window.scrollY || window.pageYOffset || 0;
    const toolbar = document.getElementById('story-floating-toolbar');
    if (!toolbar) {
        scrollTicking = false;
        return;
    }

    const isMobile = (window.innerWidth < 768);

    if (isMobile) {
        if (currentY <= 60) {
            toolbar.classList.remove('toolbar-hidden');
            lastScrollY = currentY;
            scrollTicking = false;
            return;
        }

        const deltaY = currentY - lastScrollY;
        // Scrolling down -> hide smoothly
        if (deltaY > 12 && currentY > 100) {
            if (!toolbar.classList.contains('toolbar-hidden')) {
                toolbar.classList.add('toolbar-hidden');
                closeStoryReaderPopover();
            }
        }
        // Scrolling up -> reveal smoothly
        else if (deltaY < -10) {
            if (toolbar.classList.contains('toolbar-hidden')) {
                toolbar.classList.remove('toolbar-hidden');
            }
        }
    } else {
        // iPad & Desktop: Always floating fixed
        toolbar.classList.remove('toolbar-hidden');
    }

    // Dismiss open popover when actively scrolling
    if (Math.abs(currentY - lastScrollY) > 24) {
        closeStoryReaderPopover();
    }

    lastScrollY = currentY;
    scrollTicking = false;
}

/**
 * Initialize Reader Toolbar Module
 */
export function initReaderToolbar() {
    // 1. Scroll listener with requestAnimationFrame
    window.addEventListener('scroll', () => {
        if (!scrollTicking) {
            window.requestAnimationFrame(handleSmartScroll);
            scrollTicking = true;
        }
    }, { passive: true });

    // 2. Click outside listener to dismiss Popover
    document.addEventListener('click', (e) => {
        const popover = document.getElementById('apple-reader-popover');
        const aaBtn = document.getElementById('story-aa-btn');
        if (!popover || !popover.classList.contains('show')) return;

        if (!popover.contains(e.target) && !aaBtn.contains(e.target)) {
            closeStoryReaderPopover();
        }
    });

    // 3. Restore saved font family
    try {
        const savedFamily = localStorage.getItem('dieukydieu_font_family');
        if (savedFamily === 'sans') {
            setStoryFontFamily('sans');
        }
        const savedLevel = parseInt(localStorage.getItem('dieukydieu_font_level'), 10);
        if (!isNaN(savedLevel)) {
            updateReaderPopoverUI(savedLevel);
        }
    } catch (_) {}
}

// Global exposure for inline onclick handlers in HTML
if (typeof window !== 'undefined') {
    window.toggleStoryReaderPopover = toggleStoryReaderPopover;
    window.closeStoryReaderPopover = closeStoryReaderPopover;
    window.setStoryFontSizeStep = setStoryFontSizeStep;
    window.setStoryFontFamily = setStoryFontFamily;
    window.handleStoryAudioPlay = handleStoryAudioPlay;
    window.updateReaderPopoverUI = updateReaderPopoverUI;
    window.stopStorySpeech = stopStorySpeech;
}
